export type RangePreset =
  | "7d"
  | "28d"
  | "30d"
  | "MTD"
  | "90d"
  | "120d"
  | "YTD"
  | "1y"
  | "3y"
  | "all"
  | "custom";

export interface SocialRange {
  preset: RangePreset;
  /** Solo para `custom` (YYYY-MM-DD). */
  desde?: string;
  hasta?: string;
}

export const DEFAULT_RANGE: SocialRange = { preset: "MTD" };

export const PRESET_LABELS: Record<RangePreset, string> = {
  "7d": "7 días",
  "28d": "28 días",
  "30d": "30 días",
  MTD: "Mes a la fecha (MTD)",
  "90d": "90 días",
  "120d": "120 días",
  YTD: "Año a la fecha (YTD)",
  "1y": "1 año",
  "3y": "3 años",
  all: "Todo el historial",
  custom: "Personalizado",
};

const STORAGE_KEY = "wolf.active.range";
const LEGACY_STORAGE_KEY = "wolf.social.range";

/** Fecha de hoy en Santiago, como YYYY-MM-DD. */
export function todaySantiago(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Santiago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function shiftDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Rango efectivo en fechas. `null` en ambos extremos = sin filtro de fecha. */
export function resolveRange(range: SocialRange): { desde: string | null; hasta: string | null } {
  const today = todaySantiago();
  switch (range.preset) {
    case "7d":
      return { desde: shiftDays(today, -6), hasta: today };
    case "28d":
      return { desde: shiftDays(today, -27), hasta: today };
    case "30d":
      return { desde: shiftDays(today, -29), hasta: today };
    case "MTD":
      return { desde: `${today.slice(0, 7)}-01`, hasta: today };
    case "90d":
      return { desde: shiftDays(today, -89), hasta: today };
    case "120d":
      return { desde: shiftDays(today, -119), hasta: today };
    case "YTD":
      return { desde: `${today.slice(0, 4)}-01-01`, hasta: today };
    case "1y":
      return { desde: shiftDays(today, -364), hasta: today };
    case "3y":
      return { desde: shiftDays(today, -1094), hasta: today };
    case "all":
      return { desde: null, hasta: null };
    case "custom": {
      const desde = range.desde || null;
      const hasta = range.hasta || null;
      if (desde && hasta && desde > hasta) return { desde: hasta, hasta: desde };
      return { desde, hasta };
    }
  }
}

/** Parámetros para el backend Social: "todo" se pide con un inicio anterior, que el backend trata como historial completo. */
export function rangeToQuery(range: SocialRange): { desde?: string; hasta?: string } {
  if (range.preset === "all") return { desde: "2000-01-01", hasta: todaySantiago() };
  const { desde, hasta } = resolveRange(range);
  const out: { desde?: string; hasta?: string } = {};
  if (desde) out.desde = desde;
  if (hasta) out.hasta = hasta;
  return out;
}

/** Convierte el rango activo a parámetro de ventana (`window`) para endpoints SEO / Paid (`client-detail`). */
export function rangeToWindow(range: SocialRange): string {
  if (range.preset === "custom") {
    const { desde, hasta } = resolveRange(range);
    const d = desde || "2020-01-01";
    const h = hasta || todaySantiago();
    return `custom:${d}:${h}`;
  }
  return range.preset;
}

export function readStoredRange(): SocialRange {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY) || window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return DEFAULT_RANGE;
    const parsed = JSON.parse(raw) as SocialRange;
    if (parsed && parsed.preset in PRESET_LABELS) return parsed;
  } catch {
    /* sin almacenamiento o dato inválido: valor por defecto */
  }
  return DEFAULT_RANGE;
}

export function storeRange(range: SocialRange): void {
  try {
    const serialized = JSON.stringify(range);
    window.localStorage.setItem(STORAGE_KEY, serialized);
    window.localStorage.setItem(LEGACY_STORAGE_KEY, serialized);
  } catch {
    /* ignorar */
  }
}

